import type { ChannelInfo, PublicUser, Space } from "@rhema/client";
import {
  Button,
  CheckIcon,
  CopyIcon,
  cn,
  HashIcon,
  LinkIcon,
  ListRow,
  LockIcon,
  Sheet,
  Switch,
  TextField,
  TrashIcon,
  UserPlusIcon,
} from "@rhema/ui";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { UpgradeLink, useFailure } from "../billing/UpgradeLink";
import { initialsFor, toneFor } from "../data/view";
import { useFeedback } from "../menus/Feedback";
import { PeoplePicker } from "../people/PeoplePicker";
import { PhotoPicker } from "../people/PhotoPicker";
import { ShareTargets } from "../people/ShareTargets";
import { useApi, useClient, useSession } from "../session/session";
import { inviteCodeFrom, isAdmin, sharedNote, spaceHref, spaceInviteLink } from "./data";

function Failure({ error, upgrade }: { error: string | null; upgrade: boolean }) {
  if (!error) return null;
  return (
    <p role="alert" className="m-0 rounded-field bg-scarlet-tint px-3.5 py-2.5 text-sm text-scarlet">
      {error}
      {upgrade && <UpgradeLink />}
    </p>
  );
}

function Option({
  title,
  detail,
  checked,
  onChange,
}: {
  title: string;
  detail: string;
  checked: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="text-[15px] font-semibold">{title}</span>
        <span className="text-[12.5px] text-ink-secondary">{detail}</span>
      </span>
      <Switch label={title} checked={checked} onChange={onChange} />
    </div>
  );
}

/** Starts a space. Its #general channel is made with it, and the invite step follows. */
export function CreateSpaceSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const client = useClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [about, setAbout] = useState("");
  const [busy, setBusy] = useState(false);
  const { error, upgrade, fail, clear } = useFailure();

  const close = () => {
    setName("");
    setAbout("");
    clear();
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    clear();
    try {
      const space = await client.createSpace(name.trim(), about.trim());
      close();
      navigate(`${spaceHref(space.id)}?invite=1`);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={close} title="Create a space" className="px-5 pb-7">
      <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
        <p className="m-0 text-sm leading-normal text-ink-secondary">
          A space holds channels for a team, a church or a club. It starts with #general, which everyone who
          joins is in.
        </p>
        <TextField
          label="Name"
          required
          maxLength={80}
          placeholder="Acme Limited"
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <TextField
          label="What it is for (optional)"
          maxLength={280}
          placeholder="Our team's messages and updates"
          value={about}
          onChange={(e) => setAbout(e.target.value)}
        />
        <Failure error={error} upgrade={upgrade} />
        <Button type="submit" size="lg" disabled={busy || !name.trim()}>
          {busy ? "Creating the space" : "Create space"}
        </Button>
      </form>
    </Sheet>
  );
}

/** Joins a space from a pasted invite link or code. */
export function JoinSpaceSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const [text, setText] = useState("");
  const code = inviteCodeFrom(text);
  const close = () => {
    setText("");
    onClose();
  };
  return (
    <Sheet open={open} onClose={close} title="Join a space" className="px-5 pb-7">
      <form
        className="mt-4 flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (!code) return;
          close();
          navigate(`/spaces/join/${code}`);
        }}
      >
        <p className="m-0 text-sm leading-normal text-ink-secondary">
          Paste the invite link someone in the space sent you.
        </p>
        <TextField
          label="Invite link"
          placeholder="https://app.rhema.siliconblast.com/spaces/join/…"
          value={text}
          onChange={(e) => setText(e.target.value)}
          autoFocus
        />
        <Button type="submit" size="lg" disabled={!code}>
          Continue
        </Button>
      </form>
    </Sheet>
  );
}

/** The space's invite link, for admins: copy it, share it, or turn it off. */
export function SpaceInviteSheet({
  space,
  open,
  onClose,
}: {
  space: Space;
  open: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const client = useClient();
  const { platform } = useSession();
  const [code, setCode] = useState<string | null>(space.invite_code);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const { error, upgrade, fail, clear } = useFailure();
  const origin = platform.serverOrigin() || window.location.origin;
  const link = code ? spaceInviteLink(code, origin) : null;

  useEffect(() => setCode(space.invite_code), [space.invite_code]);

  const run = async (job: () => Promise<void>) => {
    setBusy(true);
    clear();
    try {
      await job();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const make = () =>
    run(async () => {
      setCode((await api.spaceInvite(space.id)).code);
      setCopied(false);
      await client.refreshSpaces();
    });

  const revoke = () =>
    run(async () => {
      await api.revokeSpaceInvite(space.id);
      setCode(null);
      await client.refreshSpaces();
    });

  return (
    <Sheet open={open} onClose={onClose} title={`Invite people to ${space.name}`} className="px-5 pb-7">
      <div className="mt-4 flex flex-col gap-3">
        <p className="m-0 text-sm leading-normal text-ink-secondary">
          Anyone with the link can join the space and its #general channel. Turn the link off to stop new
          people joining with it.
        </p>
        <Failure error={error} upgrade={upgrade} />
        {link ? (
          <>
            <div className="flex items-center gap-2 rounded-field border border-line bg-surface px-3.5 py-3">
              <LinkIcon size={17} className="flex-none text-ink-secondary" />
              <span className="min-w-0 flex-1 truncate text-sm">{link}</span>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                icon={copied ? <CheckIcon size={17} /> : <CopyIcon size={17} />}
                onClick={() =>
                  void navigator.clipboard
                    ?.writeText(link)
                    .then(() => setCopied(true))
                    .catch(() => undefined)
                }
              >
                {copied ? "Copied" : "Copy link"}
              </Button>
              <Button variant="ghost" className="text-scarlet" disabled={busy} onClick={() => void revoke()}>
                Turn off the link
              </Button>
            </div>
            <ShareTargets
              text={`Join ${space.name} on Rhema: ${link}`}
              url={link}
              subject={`Join ${space.name} on Rhema`}
              onResult={(r) => r.note?.startsWith("Copied") && setCopied(true)}
            />
          </>
        ) : (
          <Button size="lg" disabled={busy} icon={<LinkIcon size={18} />} onClick={() => void make()}>
            {busy ? "Making the link" : "Make an invite link"}
          </Button>
        )}
      </div>
    </Sheet>
  );
}

/** Makes a channel. Admins can make it an announcement channel or put everyone in it. */
export function CreateChannelSheet({
  space,
  open,
  onClose,
}: {
  space: Space;
  open: boolean;
  onClose: () => void;
}) {
  const client = useClient();
  const navigate = useNavigate();
  const admin = isAdmin(space);
  const [name, setName] = useState("");
  const [topic, setTopic] = useState("");
  const [locked, setLocked] = useState(false);
  const [announcement, setAnnouncement] = useState(false);
  const [everyone, setEveryone] = useState(false);
  const [busy, setBusy] = useState(false);
  const { error, upgrade, fail, clear } = useFailure();
  const preview = name
    .trim()
    .toLowerCase()
    .replace(/[\s_]+/g, "-")
    .replace(/[^\p{L}\p{N}-]/gu, "");

  const close = () => {
    setName("");
    setTopic("");
    setLocked(false);
    setAnnouncement(false);
    setEveryone(false);
    clear();
    onClose();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    clear();
    try {
      const chat = await client.createChannel(space.id, {
        name: name.trim(),
        topic: topic.trim(),
        private: locked,
        announcement,
        everyone: everyone && !locked,
      });
      close();
      navigate(spaceHref(space.id, chat.id));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet open={open} onClose={close} title="Create a channel" className="px-5 pb-7">
      <form onSubmit={submit} className="mt-4 flex flex-col gap-4">
        <TextField
          label="Name"
          required
          maxLength={80}
          placeholder="project-updates"
          hint={preview ? `It shows as #${preview}.` : "Lower case, with hyphens for spaces."}
          value={name}
          onChange={(e) => setName(e.target.value)}
          autoFocus
        />
        <TextField
          label="Topic (optional)"
          maxLength={280}
          placeholder="Weekly progress and decisions"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
        />
        <Option
          title="Private"
          detail="Only people added to it can see it or read it."
          checked={locked}
          onChange={setLocked}
        />
        {admin && (
          <Option
            title="Announcements"
            detail="Only admins post. Everyone can reply in threads."
            checked={announcement}
            onChange={setAnnouncement}
          />
        )}
        {admin && !locked && (
          <Option
            title="Everyone in the space"
            detail="Puts everyone in it now, and everyone who joins later."
            checked={everyone}
            onChange={setEveryone}
          />
        )}
        <Failure error={error} upgrade={upgrade} />
        <Button type="submit" size="lg" disabled={busy || !name.trim()}>
          {busy ? "Creating the channel" : "Create channel"}
        </Button>
      </form>
    </Sheet>
  );
}

/** Every public channel, and the private ones you are in, to open or join. */
export function BrowseChannelsSheet({
  space,
  open,
  onClose,
  onCreate,
}: {
  space: Space;
  open: boolean;
  onClose: () => void;
  onCreate: () => void;
}) {
  const api = useApi();
  const client = useClient();
  const navigate = useNavigate();
  const [list, setList] = useState<ChannelInfo[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const { error, upgrade, fail, clear } = useFailure();

  // biome-ignore lint/correctness/useExhaustiveDependencies: loads once each time it opens
  useEffect(() => {
    if (!open) return;
    setList(null);
    api
      .channels(space.id)
      .then(setList)
      .catch((err) => fail(err));
  }, [api, open, space.id]);

  const openChannel = async (c: ChannelInfo) => {
    clear();
    if (c.joined) {
      onClose();
      navigate(spaceHref(space.id, c.id));
      return;
    }
    setBusy(c.id);
    try {
      await client.joinChannel(space.id, c.id);
      onClose();
      navigate(spaceHref(space.id, c.id));
    } catch (err) {
      fail(err);
    } finally {
      setBusy(null);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title={`Channels in ${space.name}`} className="px-5 pb-7">
      <div className="mt-3 flex flex-col gap-2">
        <Failure error={error} upgrade={upgrade} />
        {!list && !error && <p className="m-0 text-sm text-ink-meta">Loading the channels.</p>}
        <ul aria-label="Channels" className="m-0 flex list-none flex-col p-0">
          {list?.map((c) => (
            <li key={c.id} className="flex items-center gap-3 border-t border-line py-3 first:border-t-0">
              <span className="flex h-10 w-10 flex-none items-center justify-center rounded-tile bg-surface-header text-ink-secondary">
                {c.private ? <LockIcon size={17} /> : <HashIcon size={17} />}
              </span>
              <span className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-[15px] font-semibold">#{c.name}</span>
                <span className="truncate text-[12.5px] text-ink-secondary">
                  {[
                    c.topic,
                    `${c.member_count} ${c.member_count === 1 ? "member" : "members"}`,
                    sharedNote(c, space.id),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </span>
              <Button
                size="sm"
                variant={c.joined ? "outline" : "primary"}
                disabled={busy === c.id}
                onClick={() => void openChannel(c)}
              >
                {c.joined ? "Open" : busy === c.id ? "Joining" : "Join"}
              </Button>
            </li>
          ))}
        </ul>
        <Button
          variant="soft"
          block
          className="mt-2"
          onClick={() => {
            onClose();
            onCreate();
          }}
        >
          Create a channel
        </Button>
      </div>
    </Sheet>
  );
}

function Confirm({
  title,
  detail,
  action,
  busy,
  onConfirm,
  onCancel,
  children,
}: {
  title: string;
  detail: string;
  action: string;
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3 rounded-card border border-line bg-surface p-4">
      <h3 className="m-0 text-[15px] font-bold">{title}</h3>
      <p className="m-0 text-sm leading-normal text-ink-secondary">{detail}</p>
      {children}
      <div className="flex gap-2">
        <Button variant="danger" disabled={busy} onClick={onConfirm}>
          {action}
        </Button>
        <Button variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

/** The space's name and description for admins, and leaving or deleting it. */
export function SpaceSettingsSheet({
  space,
  open,
  onClose,
}: {
  space: Space;
  open: boolean;
  onClose: () => void;
}) {
  const api = useApi();
  const client = useClient();
  const navigate = useNavigate();
  const admin = isAdmin(space);
  const owner = space.role === "owner";
  const [name, setName] = useState(space.name);
  const [about, setAbout] = useState(space.about);
  const [step, setStep] = useState<"edit" | "leave" | "delete">("edit");
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const { error, upgrade, fail, clear } = useFailure();

  useEffect(() => {
    setName(space.name);
    setAbout(space.about);
  }, [space.name, space.about]);

  const close = () => {
    setStep("edit");
    setTyped("");
    clear();
    onClose();
  };

  const run = async (job: () => Promise<void>) => {
    setBusy(true);
    clear();
    try {
      await job();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const save = (e: FormEvent) => {
    e.preventDefault();
    void run(async () => {
      await api.updateSpace(space.id, { name: name.trim(), about: about.trim() });
      await client.refreshSpaces();
      close();
    });
  };

  const leave = (deleting: boolean) =>
    run(async () => {
      await client.leaveSpace(space.id, deleting);
      close();
      navigate("/spaces");
    });

  return (
    <Sheet open={open} onClose={close} title={space.name} subtitle="Space settings" className="px-5 pb-7">
      <div className="mt-4 flex flex-col gap-4">
        <Failure error={error} upgrade={upgrade} />
        {admin && step === "edit" && (
          <PhotoPicker
            name={space.name}
            src={space.avatar_url}
            initials={initialsFor(space.name)}
            tone={toneFor(space.id)}
            label={space.name}
            onPick={async (photo) => {
              await api.setSpacePhoto(space.id, photo);
              await client.refreshSpaces();
            }}
            onRemove={async () => {
              await api.removeSpacePhoto(space.id);
              await client.refreshSpaces();
            }}
          />
        )}
        {admin && step === "edit" && (
          <form onSubmit={save} className="flex flex-col gap-3">
            <TextField
              label="Name"
              required
              maxLength={80}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              label="What it is for"
              maxLength={280}
              value={about}
              onChange={(e) => setAbout(e.target.value)}
            />
            <Button type="submit" disabled={busy || !name.trim()}>
              {busy ? "Saving" : "Save"}
            </Button>
          </form>
        )}
        {step === "edit" && (
          <div className="flex flex-col gap-2 border-t border-line pt-4">
            <Button variant="danger" onClick={() => setStep("leave")}>
              Leave the space
            </Button>
            {owner && (
              <Button
                variant="ghost"
                className="text-scarlet"
                icon={<TrashIcon size={17} />}
                onClick={() => setStep("delete")}
              >
                Delete the space
              </Button>
            )}
          </div>
        )}
        {step === "leave" && (
          <Confirm
            title={`Leave ${space.name}?`}
            detail={
              owner
                ? "You leave every channel in it. The space passes to its longest-serving admin, or else its longest-serving member."
                : "You leave every channel in it. An invite link brings you back."
            }
            action={busy ? "Leaving" : "Leave"}
            busy={busy}
            onConfirm={() => void leave(false)}
            onCancel={() => setStep("edit")}
          />
        )}
        {step === "delete" && (
          <Confirm
            title={`Delete ${space.name}?`}
            detail="Every channel, message and file in it is deleted for everyone. This cannot be undone."
            action={busy ? "Deleting" : "Delete space"}
            busy={busy || typed.trim() !== space.name}
            onConfirm={() => void leave(true)}
            onCancel={() => {
              setStep("edit");
              setTyped("");
            }}
          >
            <TextField
              label={`Type ${space.name} to confirm`}
              autoComplete="off"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
            />
          </Confirm>
        )}
      </div>
    </Sheet>
  );
}

/**
 * Invites guests, who are in the channels picked here and no others. Admins add people from
 * their contacts, or make a guest link to send. Private channels show only if you are in them.
 */
export function GuestInviteSheet({
  space,
  open,
  onClose,
  onAdded,
}: {
  space: Space;
  open: boolean;
  onClose: () => void;
  onAdded?: () => void;
}) {
  const api = useApi();
  const feedback = useFeedback();
  const { platform } = useSession();
  const [step, setStep] = useState<"channels" | "people" | "link">("channels");
  const [list, setList] = useState<ChannelInfo[] | null>(null);
  const [picked, setPicked] = useState<string[]>([]);
  const [people, setPeople] = useState<PublicUser[]>([]);
  const [link, setLink] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const { error, upgrade, fail, clear } = useFailure();
  const origin = platform.serverOrigin() || window.location.origin;

  // biome-ignore lint/correctness/useExhaustiveDependencies: loads once each time it opens
  useEffect(() => {
    if (!open) return;
    setList(null);
    api
      .channels(space.id)
      .then(setList)
      .catch((err) => fail(err));
  }, [api, open, space.id]);

  const close = () => {
    setStep("channels");
    setPicked([]);
    setPeople([]);
    setLink(null);
    setCopied(false);
    clear();
    onClose();
  };

  const run = async (job: () => Promise<void>) => {
    setBusy(true);
    clear();
    try {
      await job();
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const toggle = (id: string) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const names = (list ?? []).filter((c) => picked.includes(c.id)).map((c) => `#${c.name}`);

  const add = () =>
    run(async () => {
      const out = await api.addGuests(
        space.id,
        people.map((p) => p.id),
        picked,
      );
      const added = out.added.length;
      const skipped = out.skipped.length;
      feedback.notify(
        skipped
          ? `Added ${added}. ${skipped} could not be added because of their privacy settings.`
          : `Added ${added} ${added === 1 ? "guest" : "guests"}.`,
      );
      onAdded?.();
      close();
    });

  const makeLink = () =>
    run(async () => {
      const invite = await api.createGuestInvite(space.id, picked);
      setLink(spaceInviteLink(invite.code, origin));
      setStep("link");
    });

  return (
    <Sheet open={open} onClose={close} title="Invite a guest" subtitle={space.name} className="px-5 pb-7">
      <div className="mt-3 flex flex-col gap-3">
        <Failure error={error} upgrade={upgrade} />
        {step === "channels" && (
          <>
            <p className="m-0 text-sm leading-normal text-ink-secondary">
              Guests are only in the channels you pick. They cannot browse other channels or see everyone in
              the space.
            </p>
            {!list && !error && <p className="m-0 text-sm text-ink-meta">Loading the channels.</p>}
            <ul aria-label="Channels for the guest" className="m-0 flex list-none flex-col p-0">
              {list?.map((c) => {
                const on = picked.includes(c.id);
                const note = sharedNote(c, space.id);
                return (
                  <li key={c.id}>
                    <ListRow
                      onClick={() => toggle(c.id)}
                      selected={on}
                      className={cn("rounded-tile px-2", on && "bg-blue-tint")}
                      leading={
                        <span className="flex h-9 w-9 flex-none items-center justify-center rounded-tile bg-surface-header text-ink-secondary">
                          {c.private ? <LockIcon size={16} /> : <HashIcon size={16} />}
                        </span>
                      }
                      title={c.name}
                      subtitle={note ? <span className="truncate">{note}</span> : undefined}
                      trailing={
                        on ? (
                          <CheckIcon size={18} strokeWidth={2.6} className="text-blue" label="Picked" />
                        ) : undefined
                      }
                    />
                  </li>
                );
              })}
            </ul>
            <div className="flex flex-wrap gap-2">
              <Button
                disabled={!picked.length}
                icon={<UserPlusIcon size={17} />}
                onClick={() => setStep("people")}
              >
                Add from contacts
              </Button>
              <Button
                variant="outline"
                disabled={!picked.length || busy}
                icon={<LinkIcon size={17} />}
                onClick={() => void makeLink()}
              >
                {busy ? "Making the link" : "Make a guest link"}
              </Button>
            </div>
          </>
        )}
        {step === "people" && (
          <>
            <p className="m-0 text-sm leading-normal text-ink-secondary">
              They join as guests in {names.join(", ")}.
            </p>
            <PeoplePicker
              multiple
              selected={people.map((p) => p.id)}
              onPick={(p) =>
                setPeople((all) =>
                  all.some((x) => x.id === p.id) ? all.filter((x) => x.id !== p.id) : [...all, p],
                )
              }
            />
            <div className="flex flex-wrap gap-2">
              <Button disabled={!people.length || busy} onClick={() => void add()}>
                {busy
                  ? "Adding"
                  : people.length
                    ? `Add ${people.length} as ${people.length === 1 ? "a guest" : "guests"}`
                    : "Add as guests"}
              </Button>
              <Button variant="ghost" onClick={() => setStep("channels")}>
                Back
              </Button>
            </div>
          </>
        )}
        {step === "link" && link && (
          <>
            <p className="m-0 text-sm leading-normal text-ink-secondary">
              Anyone with this link joins {space.name} as a guest in {names.join(", ")}. You can turn it off
              on the Manage channels page.
            </p>
            <div className="flex items-center gap-2 rounded-field border border-line bg-surface px-3.5 py-3">
              <LinkIcon size={17} className="flex-none text-ink-secondary" />
              <span className="min-w-0 flex-1 truncate text-sm select-text">{link}</span>
            </div>
            <Button
              className="self-start"
              icon={copied ? <CheckIcon size={17} /> : <CopyIcon size={17} />}
              onClick={() =>
                void navigator.clipboard
                  ?.writeText(link)
                  .then(() => setCopied(true))
                  .catch(() => undefined)
              }
            >
              {copied ? "Copied" : "Copy link"}
            </Button>
            <ShareTargets
              text={`Join ${space.name} on Rhema as a guest: ${link}`}
              url={link}
              subject={`Join ${space.name} on Rhema`}
              onResult={(r) => r.note?.startsWith("Copied") && setCopied(true)}
            />
          </>
        )}
      </div>
    </Sheet>
  );
}
